import React, { useEffect, useState } from 'react';

import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Description,
} from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import moment from 'moment';

import { format, isToday } from 'date-fns';
import {
  Calendar,
  EventProps,
  momentLocalizer,
  ToolbarProps,
  Views,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { useCalendarStyles } from './styles';
import dayjs from 'dayjs';
import { ICalendarEvent } from '../../../global/interfaces';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import { CalendarEvent } from '../../../global/interfaces';
import { COLORS } from '../../../utils/colors';
import AllEventsModal from './AllEventsModal';
import DeleteBookingModal from './DeleteBookingModal';
import SearchResultsModal from '../SearchResultsModal';
import SessionSummaryModal from '../SessionSummaryModal';
import CalendarGlobalStyles from '../CalendarSchedule/styles/CalendarGlobalStyles';
import CalendarSideMenu from '../CalendarSchedule/CalendarSideMenu';
import useBookings from '../../../hooks/useBookings';

interface CustomToolbarProps extends ToolbarProps<ICalendarEvent, object> {
  onOpenFilters: () => void;
  calView: boolean;
  setCalView: (value: boolean) => void;
  periodView: string;
  setPeriodView: (value: string) => void;
  setToday: () => void;
}

interface CustomEventProps extends EventProps {
  event: CalendarEvent;
  view: string;
}

interface CustomCellProps {
  value: Date;
  moreEvents: CalendarEvent[];
  onShowMoreClick: (date: Date, clickEvent: React.MouseEvent, day: any) => void;
  periodView: string;
}

const eventPropGetter = (event: any) => {
  const style = {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0px',
    display: 'flex',
    alignItems: 'center',
    color: COLORS.DARK_GREY,
    fontSize: '10px',
    overflow: 'visible',
    whiteSpace: 'nowrap',
  };

  return {
    style: style,
  };
};

export const CustomToolbar: React.FC<CustomToolbarProps> = ({
  onNavigate,
  label,
  calView,
  setCalView,
  periodView,
  setPeriodView,
  setToday,
  onOpenFilters,
}) => {
  const styles = useCalendarStyles();

  return (
    <Box sx={styles.toolbarContainer}>
      <Box sx={styles.navigationContainer}>
        <Box marginRight={'auto'}>
          <Button onClick={setToday} sx={styles.today}>
            Today
          </Button>
        </Box>
        <Box sx={styles.customToolbarContainer}>
          <Button
            onClick={() => onNavigate('PREV')}
            sx={{ minWidth: '1rem', color: COLORS.DARK_GREY }}>
            <ChevronLeft />
          </Button>
          <Button
            onClick={() => onNavigate('NEXT')}
            sx={{ minWidth: '1rem', color: COLORS.DARK_GREY }}>
            <ChevronRight />
          </Button>
          <Typography sx={styles.navigationTitle} margin={'1.375rem 1.813rem'}>
            {label}
          </Typography>
        </Box>
      </Box>
      <Box display={'flex'} alignItems={'center'}>
        <CalendarMonth sx={{ color: calView ? COLORS.PRIMARY : '#53617a' }} />
        <Switch
          sx={styles.switchStyles}
          checked={!calView}
          onChange={() => setCalView(!calView)}
        />
        <Description
          sx={{
            color: !calView ? COLORS.PRIMARY : '#53617a',
            marginRight: '1rem',
          }}
        />
      </Box>
      <Select
        label={'Month'}
        size="small"
        sx={{
          borderRadius: '3rem',
          minWidth: '10rem',
          '.MuiOutlinedInput-notchedOutline': {
            border: '1px solid #464e5f',
          },
        }}
        defaultValue="month"
        value={periodView}
        onChange={(e) => setPeriodView(e.target.value)}>
        <MenuItem value="month">Month</MenuItem>
        <MenuItem value="week">Week - 5 Days</MenuItem>
        <MenuItem value="day">Day</MenuItem>
      </Select>
    </Box>
  );
};

const CustomEvent = ({ event, view }: CustomEventProps) => {
  const styles = useCalendarStyles();
  const { setSelectedBooking, setIsSessionSummaryModalOpen, getIconStyle } =
    useBookingsContext();

  const handleEventClick = () => {
    setSelectedBooking(event.details);
    setIsSessionSummaryModalOpen(true);
  };

  return (
    <Box sx={styles.eventContainer} onClick={handleEventClick}>
      {view === 'month' ? (
        <>
          <Box sx={getIconStyle(event.title)}></Box>
          <Typography color={COLORS.DARK_GREY} sx={styles.eventDate}>
            {`${dayjs(event.start).format('hh:mm A')}`}
          </Typography>
          <Typography sx={styles.eventTitle} color={COLORS.DARK_GREY}>
            {event.title}
          </Typography>
        </>
      ) : null}
      {(view === 'week' || view === 'day') && (
        <Box sx={styles.weekDayViewContainer}>
          <Typography
            color={COLORS.DARK_GREY}
            sx={{
              ...styles.weekDayViewTitle,
              marginLeft: view === 'week' ? '0.5rem' : '2rem',
            }}>
            {event.title}
          </Typography>
          <Typography
            sx={{
              ...styles.weekDayViewTime,
              marginLeft: view === 'week' ? '0.5rem' : '2rem',
            }}
            color={COLORS.DARK_GREY}>
            {`${dayjs(event.start).format('hh:mm A')}`} -{' '}
            {`${dayjs(event.end).format('hh:mm A')}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const CustomCell = ({
  value,
  moreEvents,
  onShowMoreClick,
  periodView,
}: CustomCellProps) => {
  const MAX_EVENTS_TO_DISPLAY = 3;
  const styles = useCalendarStyles();

  const day = format(value, 'd');
  const isCurrentDay = isToday(value);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}>
        <Box sx={styles.customCellContainer}>
          <Typography
            sx={{
              ...styles.dayStyles,
              backgroundColor:
                isCurrentDay && periodView === 'month' ? '#0b1339' : 'inherit',
              color: isCurrentDay ? '#fff' : 'inherit',
            }}
            variant="body2">
            {day}
          </Typography>
        </Box>
      </div>
      {moreEvents.length > MAX_EVENTS_TO_DISPLAY && (
        <Button
          sx={styles.showMoreButton}
          variant="text"
          color="primary"
          onClick={(e) => onShowMoreClick(value, e, day)}>
          {`View all ${moreEvents.length} events`}
        </Button>
      )}
    </>
  );
};

const localizer = momentLocalizer(moment);

interface ICalendarProps {
  events: ICalendarEvent[];
}

const CalendarSchedule = ({ events }: ICalendarProps) => {
  const styles = useCalendarStyles();
  const {
    selectedBooking,
    isSessionSummaryModalOpen,
    setIsSessionSummaryModalOpen,
    setBookingsFilter,
    toolbarLabel,
    setToolbarLabel,
    isDeleteBookingModalOpen,
    setIsDeleteBookingModalOpen,
    filters,
    setFilters,
    handleToolbarNavigate,
    currentDate,
    setCurrentDate,
  } = useBookingsContext();

  const [view, setView] = useState<any>(Views.MONTH);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>(
    [],
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState({ date: '', day: '' });
  const [currentMonth, setCurrentMonth] = useState<string>(
    moment().format('MMMM YYYY'),
  );
  const [openFiltersPage, setOpenFiltersPage] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [calView, setCalView] = useState(true);
  const [periodView, setPeriodView] = useState('month');

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
    setCurrentMonth(moment(date).format('MMMM YYYY'));
    setBookingsFilter({
      startDate: new Date(date.getFullYear(), date.getMonth(), 1),
      endDate: new Date(date.getFullYear(), date.getMonth() + 1, 1),
    });
  };

  const handleOpenFilters = () => {
    setOpenFilters(true);
  };

  const handleShowMore = (date: Date, clickEvent: React.MouseEvent) => {
    const moreEvents = getMoreEvents(date);
    setSelectedDateEvents(moreEvents.eventsToDisplay);
    setShowAllEvents(true);

    setModalPosition({
      top: clickEvent.clientY - 150,
      left: clickEvent.clientX - 25,
    });
    const day = moment(date).format('D');
    const dayOfWeek = dayjs(date).format('dddd');
    setSelectedDay({ day: day, date: dayOfWeek });
  };

  const getMoreEvents = (date: Date) => {
    const eventsForDate = validEvents.filter(
      (event) =>
        moment(event.start).isSame(date, 'day') ||
        moment(event.end).isSame(date, 'day'),
    );
    const uniqueEvents = Array.from(
      new Map(
        eventsForDate.map((event) => [event.title + event.start, event]),
      ).values(),
    );
    const hasMoreEvents = uniqueEvents.length > 4;
    const eventsToDisplay = hasMoreEvents
      ? uniqueEvents.slice(0, 4)
      : uniqueEvents;

    return { eventsToDisplay, hasMoreEvents };
  };

  const handleSelectSlot = (slotInfo: { start: Date }) => {
    setSelectedDate(slotInfo.start);
  };

  const setToday = () => {
    const today = new Date();
    setSelectedDate(today);
    handleNavigate(today);
  };

  useEffect(() => {
    if (periodView === 'month') {
      setCalView(true);
      setView('month');
    }
    if (periodView === 'week') {
      setCalView(true);
      setView('work_week');
    }
    if (periodView === 'day') {
      setCalView(true);
      setView('day');
    }
  }, [periodView]);

  const validEvents = events.filter((event) => event?.title && event?.start);
  return (
    <>
      <CalendarSideMenu
        isReportsPage={false}
        setOpenFilters={setOpenFiltersPage}
        openFilters={openFiltersPage}
        filters={filters}
        setFilters={setFilters}
        currentDate={selectedDate}
        setCurrentDate={setSelectedDate}
        setCalView={setCalView}
      />

      <Box
        className={`calendar-container ${periodView}-view`}
        sx={{
          ...styles.calendarContainer,
        }}>
        {<CalendarGlobalStyles />}
        {calView ? (
          <Calendar
            localizer={localizer}
            events={validEvents}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            onSelectSlot={handleSelectSlot}
            selectable
            dayPropGetter={(date) => ({
              style: {
                border: moment(date).isSame(selectedDate, 'day')
                  ? `2px solid #0b1339`
                  : 'inherit',
                borderRadius: moment(date).isSame(selectedDate, 'day')
                  ? '1rem'
                  : 'inherit',
              },
            })}
            views={['month', 'day', 'work_week']}
            view={view}
            onView={setView}
            onNavigate={handleNavigate}
            defaultDate={currentDate}
            style={{ height: '100vh' }}
            eventPropGetter={eventPropGetter}
            components={{
              event: (eventProps) => {
                return <CustomEvent {...eventProps} view={periodView} />;
              },
              toolbar: (props: ToolbarProps<ICalendarEvent, object>) => {
                setToolbarLabel(props.label);

                return (
                  <CustomToolbar
                    {...props}
                    onNavigate={(action, newDate) => {
                      if (view === 'month') {
                        props.onNavigate(action, newDate);
                        handleToolbarNavigate(action, newDate);
                      } else {
                        props.onNavigate(action, newDate);
                      }
                    }}
                    label={props.label}
                    onOpenFilters={handleOpenFilters}
                    calView={calView}
                    setCalView={setCalView}
                    periodView={periodView}
                    setPeriodView={setPeriodView}
                    setToday={setToday}
                  />
                );
              },
              month: {
                dateHeader: (props: any) => {
                  const { eventsToDisplay } = getMoreEvents(props.date);
                  return (
                    <CustomCell
                      periodView={periodView}
                      value={props.date}
                      moreEvents={eventsToDisplay}
                      onShowMoreClick={(
                        date: Date,
                        e: React.MouseEvent<Element>,
                      ) => handleShowMore(date, e)}
                    />
                  );
                },
              },
            }}
          />
        ) : null}
        {!calView ? (
          <SearchResultsModal
            calView={calView}
            setCalView={setCalView}
            openFiltersModal={() => setOpenFilters(true)}
            events={validEvents}
            onNavigate={handleToolbarNavigate}
            label={toolbarLabel}
            periodView={periodView}
            setPeriodView={setPeriodView}
          />
        ) : null}

        <AllEventsModal
          events={selectedDateEvents}
          open={showAllEvents}
          onClose={() => setShowAllEvents(false)}
          position={modalPosition || { top: 50, left: 50 }}
          day={selectedDay}
        />
      </Box>

      <DeleteBookingModal
        open={isDeleteBookingModalOpen}
        onClose={() => setIsDeleteBookingModalOpen(false)}
      />
      {/* <SearchResultsModal
        calView={calView}
        onNavigate={handleToolbarNavigate}
        periodView={periodView}
        setCalView={setCalView}
        setPeriodView={setPeriodView}
        events={events}
        label={toolbarLabel}
        filterResults={filterResults}
        openFiltersModal={() => setOpenFilters(true)}
        resetFilters={resetFilters}
      /> */}
      <SessionSummaryModal
        open={isSessionSummaryModalOpen}
        onClose={() => setIsSessionSummaryModalOpen(false)}
        details={selectedBooking}
      />
    </>
  );
};
export default CalendarSchedule;
